import { fetchConfig, debounce, closeDrawer } from '@theme/theme-global';
import { dispatch } from '@theme/events';
import { SKY_THEME_EVENTS } from '@theme/config';

if (!customElements.get('buy-form')) {
  customElements.define(
    'buy-form',
    class BuyForm extends HTMLElement {
      constructor() {
        super();

        this.bundleForm = false;
        this.onSubmit = this.handleSubmit.bind(this);
        this.debouncedCenterErrorInView = debounce((errorElement) => {
          this.centerErrorInView(errorElement);
        }, 100);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        if (!this.bundleForm && this.variantInput) {
          this.variantInput.disabled = false;
        }

        this.form.removeEventListener('submit', this.onSubmit);
        this.form.addEventListener('submit', this.onSubmit);
        this.cart = document.querySelector('bag-drawer');
        this.submitButton = this.querySelector('[type="submit"][name="add"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('[data-button-label], span');

        if (document.querySelector('bag-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isPreorderItem = this.dataset.preorderItem === 'true';
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      getSubmitControls() {
        const productInfo = this.closest('buy-box');
        if (productInfo) {
          return [...productInfo.querySelectorAll('buy-form [type="submit"]')];
        }
        return this.submitButton ? [this.submitButton] : [];
      }

      setLoading(isLoading) {
        this.getSubmitControls().forEach((button) => {
          if (isLoading) {
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('is-loading');
            button.querySelector('.loading-spinner')?.classList.remove('hidden');
          } else {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-disabled');
            button.querySelector('.loading-spinner')?.classList.add('hidden');
          }
        });
      }

      handleSubmit(evt) {
        evt.preventDefault();
        if (!this.form) return;
        if (this.getSubmitControls().some((button) => button.getAttribute('aria-disabled') === 'true')) return;

        let hasError = false;
        let firstErrorField = null;

        Array.from(this.form.elements).forEach((field) => {
          if (field.getAttribute('data-required') === 'true') {
            field.classList.remove('error');

            // Remove existing inline error for this field
            const existingError =
              field.parentNode.querySelector('.inline-error-message') ||
              (field.type === 'checkbox' ? field.parentNode.parentNode.querySelector('.inline-error-message') : null);
            if (existingError) existingError.remove();

            let isError = false;

            if (field.type === 'checkbox' || field.type === 'radio') {
              if (!field.checked) isError = true;
            } else if (!field.value.trim()) {
              isError = true;
            }

            if (isError) {
              hasError = true;
              field.classList.add('error');

              const errorText = document.createElement('span');
              errorText.className = 'inline-error-message';
              errorText.innerText = window.variantStrings.requiredField;

              if (field.type === 'checkbox' || field.type === 'radio') {
                field.parentNode.parentNode.insertBefore(errorText, field.parentNode.nextSibling);
              } else {
                field.parentNode.insertBefore(errorText, field.nextSibling);
              }

              if (!firstErrorField) firstErrorField = field;
            }
          }
        });

        if (hasError) {
          this.setFormError(window.variantStrings.requiredFields);
          return;
        }

        this.setFormError();
        this.setLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (!formData.has('quantity')) {
          const sharedQuantityInput = this.closest('buy-box')?.querySelector(
            '[data-quantity-block="true"] [name="quantity"]',
          );
          const sharedQuantityValue = parseInt(sharedQuantityInput?.value, 10);
          if (!Number.isNaN(sharedQuantityValue) && sharedQuantityValue > 0) {
            formData.append('quantity', sharedQuantityValue);
          }
        }

        const giftToggle = document.querySelector(`#gift-wrap-${this.dataset.sectionId}-${this.dataset.productId}`);

        if (giftToggle && giftToggle.checked) {
          const giftProductId = giftToggle.dataset.giftProductId;
          const parentId = formData.get('id');

          if (giftProductId && parentId) {
            // detect existing items count (important)
            let index = 0;

            for (let key of formData.keys()) {
              const match = key.match(/^items\[(\d+)\]\[id\]$/);
              if (match) {
                index = Math.max(index, parseInt(match[1]) + 1);
              }
            }

            // fallback: if no indexed item found
            if (index === 0) index = 1;

            //append gift item
            formData.append(`items[${index}][id]`, giftProductId);
            formData.append(`items[${index}][parent_id]`, parentId);

            // Internal marker (underscore keeps it hidden in cart/checkout)
            formData.append(`items[${index}][properties][_gift]`, 'true');

            // Merchant-configured cart property (hidden when hide_on_cart prefixes key with _)
            const giftPropertyKey = giftToggle.dataset.giftPropertyKey?.trim();
            const giftPropertyValue = giftToggle.dataset.giftPropertyValue?.trim();
            if (giftPropertyKey && giftPropertyValue) {
              formData.append(`items[${index}][properties][${giftPropertyKey}]`, giftPropertyValue);
            }
          }
        }

        if (this.cart) {
          const sectionsToRender = this.cart.sectionsToRefresh().map((section) => section.id);
          if (this.usesStickySuccess()) {
            sectionsToRender.push('sticky-add-to-cart-success');
          }
          formData.append('sections', sectionsToRender);
          formData.append('sections_url', this.resolveSectionsPath());
          // Don't overwrite drawer return-focus when adding from inside the cart itself
          if (!this.closest('bag-drawer')) {
            this.cart.rememberFocus(this.resolveAddToCartReturnFocus());
          }
        } else if (this.usesStickySuccess()) {
          formData.append('sections', 'sticky-add-to-cart-success');
          formData.append('sections_url', this.resolveSectionsPath());
        }
        config.body = formData;

        const returnFocus = this.resolveAddToCartReturnFocus();
        const variantId = formData.get('id');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              dispatch(SKY_THEME_EVENTS.cartError, {
                source: 'buy-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });

              this.setFormError(response.description);
              this.error = true;

              // 422 can still mutate the cart (partial add / checkout limit). Refresh UI, don't open drawer.
              if (Number(response.status) === 422) {
                this.refreshCartUIAfterPartialAdd(response, {
                  variantId,
                  returnFocus,
                });
              }
              return;
            } else if (!this.cart && !this.usesStickySuccess()) {
              window.location = window.routes.cart_url;
              return;
            }

            const useStickySuccess = this.usesStickySuccess();
            if (!this.error)
              dispatch(SKY_THEME_EVENTS.cartUpdate, {
                source: 'buy-form',
                productVariantId: variantId,
                cartData: response,
                returnFocus,
              });
            this.error = false;
            this.closeOverlaysAfterAdd();
            if (this.cart) {
              this.cart.applySectionHtml(response, { skipOpen: useStickySuccess });
            } else if (!useStickySuccess) {
              window.location = window.routes.cart_url;
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.setLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.error = false;
          });
      }

      /**
       * Shopify may mutate the cart on /cart/add 422 (partial qty / add-to-cart limit)
       * while still returning an error. Keep the API error text, sync UI, do not open drawer.
       */
      refreshCartUIAfterPartialAdd(response, { variantId, returnFocus } = {}) {
        const syncCartUI = (sectionPayload, cartData) => {
          if (!cartData?.item_count && !sectionPayload?.sections) return;

          if (this.cart) {
            this.cart.classList.remove('is-empty');
            this.cart.querySelector('.drawer-inner')?.classList.remove('is-empty');
            if (sectionPayload?.sections) {
              this.cart.applySectionHtml(sectionPayload, { skipOpen: true });
            }
          } else if (sectionPayload?.sections?.['cart-count']) {
            const countEl = document.getElementById('cart-count');
            if (countEl) {
              const html = new DOMParser()
                .parseFromString(sectionPayload.sections['cart-count'], 'text/html')
                .querySelector('.shopify-section');
              if (html) countEl.innerHTML = html.innerHTML;
            }
          }

          dispatch(SKY_THEME_EVENTS.cartUpdate, {
            source: 'buy-form',
            productVariantId: variantId,
            cartData: cartData || sectionPayload,
            returnFocus,
          });
        };

        if (response?.sections && Number(response.item_count) > 0) {
          syncCartUI(response, response);
          return;
        }

        fetch(`${routes.cart_url}.js`)
          .then((res) => res.json())
          .then((cartData) => {
            if (!cartData?.item_count) return;

            return Promise.all([
              fetch(`${routes.cart_url}?section_id=cart-drawer`).then((res) => res.text()),
              fetch(`${routes.cart_url}?section_id=cart-count`).then((res) => res.text()),
            ]).then(([drawerHtml, countHtml]) => {
              syncCartUI(
                {
                  sections: {
                    'cart-drawer': drawerHtml,
                    'cart-count': countHtml,
                  },
                },
                cartData,
              );
            });
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * Element to restore focus to after cart drawer / sticky success closes.
       * When ATC is inside quick view, search, or social-media popup (which close after
       * add), return the overlay opener — the submit button will be gone or inert.
       */
      resolveAddToCartReturnFocus() {
        const quickView = this.closest('quick-view-drawer');
        if (quickView) {
          const qvTrigger = quickView._quickViewTrigger;
          if (qvTrigger && document.contains(qvTrigger) && typeof qvTrigger.focus === 'function') {
            return qvTrigger;
          }
        }

        const searchDrawer = this.closest('search-drawer');
        if (searchDrawer) {
          const searchTrigger = searchDrawer.previousFocusedElement;
          if (searchTrigger && document.contains(searchTrigger) && typeof searchTrigger.focus === 'function') {
            return searchTrigger;
          }
        }

        const socialMediaPopup = this.closest('social-media-popup');
        if (socialMediaPopup) {
          const popupOpener = socialMediaPopup._openerElement;
          if (popupOpener && document.contains(popupOpener) && typeof popupOpener.focus === 'function') {
            return popupOpener;
          }
        }

        const submit =
          this.submitButton ||
          this.querySelector('[type="submit"][name="add"]') ||
          this.querySelector('[type="submit"]');
        if (submit && document.contains(submit)) return submit;

        const active = document.activeElement;
        if (active && active !== document.body && typeof active.focus === 'function') {
          if (!this.closest('bag-drawer')?.contains(active)) return active;
        }

        return submit || null;
      }

      closeOverlaysAfterAdd() {
        const quickAddModal = this.closest('quick-view-drawer');
        if (quickAddModal?.hasAttribute('active')) {
          // Sticky / cart drawer will take focus — don't bounce to QV opener first
          quickAddModal._suppressReturnFocus = true;
          // closeDrawer unlocks page scroll; cart drawer open() will lock again if it opens.
          closeDrawer(quickAddModal);
        }

        const searchDrawer = this.closest('search-drawer');
        if (searchDrawer?.hasAttribute('active') && typeof searchDrawer.close === 'function') {
          searchDrawer._suppressReturnFocus = true;
          searchDrawer.close();
        }

        const socialMediaPopup = this.closest('social-media-popup');
        if (
          socialMediaPopup &&
          (socialMediaPopup.hasAttribute('active') || socialMediaPopup.hasAttribute('open')) &&
          typeof socialMediaPopup.close === 'function'
        ) {
          // Sticky / cart drawer / cart page own the next focus — skip popup opener restore
          socialMediaPopup.close({ restoreFocus: false });
        }
      }

      usesStickySuccess() {
        if (window.themeSettings?.enableStickyAddToCartSuccess === false) return false;
        // Cart drawer ATC (deals / recommendations) should update the drawer in place.
        if (this.closest('bag-drawer')) return false;
        return Boolean(document.querySelector('sticky-add-to-cart-success'));
      }

      resolveSectionsPath() {
        const productUrl = this.closest('buy-box')?.dataset.url;
        if (productUrl) {
          try {
            return new URL(productUrl, window.location.origin).pathname;
          } catch (error) {
            return productUrl;
          }
        }
        return window.location.pathname;
      }

      centerErrorInView(errorElement) {
        if (!errorElement) return;

        const centerErrorMessage = () => {
          if (errorElement.hidden) return false;
          const rect = errorElement.getBoundingClientRect();
          if (!rect.height) return false;
          const targetScrollTop = window.scrollY + rect.top - window.innerHeight / 2 + rect.height / 2;
          const target = Math.max(0, targetScrollTop);

          if (window.lenis?.scrollTo) {
            window.lenis.scrollTo(target, { duration: 0.8, immediate: false });
          } else {
            window.scrollTo({
              top: target,
              behavior: 'smooth',
            });
          }
          return true;
        };

        const tryCenter = (attempt = 0) => {
          const centered = centerErrorMessage();
          if (centered || attempt >= 4) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          setTimeout(() => tryCenter(attempt + 1), 60);
        };

        // Double RAF avoids first-render race with hidden->visible updates.
        requestAnimationFrame(() => requestAnimationFrame(() => tryCenter()));
      }

      setFormError(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('[data-form-error="true"]') ||
          this.closest('buy-box')?.querySelector('[data-form-error="true"]') ||
          this.closest('[data-horizontal-product-card="true"]')?.querySelector(
            '[data-horizontal-product-card-error="true"]'
          );
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessageWrapper.classList.toggle('hidden', !errorMessage);

        if (errorMessage) {
          const textEl = this.errorMessageWrapper.querySelector('[data-form-error-text]');
          if (textEl) {
            textEl.textContent = errorMessage;
          } else {
            this.errorMessageWrapper.textContent = errorMessage;
          }
          this.debouncedCenterErrorInView(this.errorMessageWrapper);
        } else {
          const textEl = this.errorMessageWrapper.querySelector('[data-form-error-text]');
          if (textEl) {
            textEl.textContent = '';
          } else {
            this.errorMessageWrapper.textContent = '';
          }
        }
      }

      setSubmitState(disable = true, text) {
        this.submitButton =
          this.submitButton ||
          this.querySelector('[type="submit"][name="add"]') ||
          this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButtonText || this.submitButton?.querySelector('[data-button-label], span');
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              text || (this.isPreorderItem ? window.variantStrings.preorderAddToCart : window.variantStrings.addToCart);
          }
        }
      }

      get variantInput() {
        return this.form?.querySelector('[name=id]');
      }
    },
  );
}
